/**
 * Goods Receipts Page
 *
 * List page for goods receipts with filters, KPIs, and drawer.
 * Supports modal form for creating new receipts (from order or free).
 * @see US-9.4 - Page Liste Receptions
 */

import { useState, useCallback, useMemo } from 'react';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { Package, Truck, FileText, AlertTriangle, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import { fetchAllForExport } from '@bill/ui';
import { Skeleton, EmptyState, KPICard } from '@/components/ui';
import type { DateRange } from '@/components/ui';
import { useDrawerRoute, useCurrency, usePermission, useCanSeeFinancials } from '@/hooks';
import { useAuth } from '@/context/AuthContext';
import { formatDateParam, parseDateParam } from '@/components/ui';
import { formatCentimes } from '@/lib/format';
import { buildExportFileName } from '@/lib/export-filename';
import { goodsReceiptsService } from '../services/goods-receipts.service';
import {
  useGoodsReceipts,
  useGoodsReceiptsKPIs,
  useValidateGoodsReceipt,
  useCancelGoodsReceipt,
  useDeleteGoodsReceipt,
  useGenerateGoodsReceiptPdf,
} from '../hooks/useGoodsReceipts';
import { GoodsReceiptsFilters } from '../components/GoodsReceiptsFilters';
import { GoodsReceiptsTable } from '../components/GoodsReceiptsTable';
import { GoodsReceiptDrawer } from '../components/GoodsReceiptDrawer';
import { GoodsReceiptFormModal } from '../components/GoodsReceiptFormModal';
import { NewReceiptButton } from '../components/NewReceiptButton';
import { SelectOrderModal } from '../components/SelectOrderModal';
import { GoodsReceiptStatus } from '@bstock/shared';
import type { GoodsReceiptListItem, QueryGoodsReceiptsDto } from '@bstock/shared';

type SortField = QueryGoodsReceiptsDto['sortBy'];
type SortOrder = QueryGoodsReceiptsDto['sortOrder'];

const DEFAULT_LIMIT = 20;

/**
 * Get initial date range (current month)
 */
function getInitialDateRange(): DateRange {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  start.setHours(0, 0, 0, 0);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

/**
 * Parse URL params into filters (deep linking)
 */
function parseUrlParams(searchParams: URLSearchParams): {
  supplierId: string;
  status: string;
  dateRange: DateRange;
  page: number;
  sortBy: SortField;
  sortOrder: SortOrder;
} {
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');
  const defaultRange = getInitialDateRange();

  return {
    supplierId: searchParams.get('supplierId') ?? '',
    status: searchParams.get('status') ?? '',
    dateRange: {
      start: startDate ? parseDateParam(startDate) : defaultRange.start,
      end: endDate ? parseDateParam(endDate) : defaultRange.end,
    },
    page: parseInt(searchParams.get('page') ?? '1', 10),
    sortBy: (searchParams.get('sortBy') as SortField) ?? 'receiptDate',
    sortOrder: (searchParams.get('sortOrder') as SortOrder) ?? 'desc',
  };
}

export default function GoodsReceiptsPage() {
  const currency = useCurrency();
  const { user } = useAuth();
  const isSuperAdmin = user?.isSuperAdmin ?? false;
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Permissions
  const canCreate = usePermission('goods-receipts.create');
  const canValidate = usePermission('goods-receipts.validate');
  const canDelete = usePermission('goods-receipts.delete');
  const canReadSuppliers = usePermission('suppliers.read');
  const showFinancials = useCanSeeFinancials();

  // Drawer routing with deep linking
  const drawer = useDrawerRoute('/achats/receptions', { paramName: 'receiptId' });

  // Deep linking state derived from URL:
  // - /achats/receptions/create?fromOrder=false => free receipt modal
  // - /achats/receptions/create?fromOrder=true => select order modal
  // - /achats/receptions/create?fromOrder=true&orderId=xxx => form modal with order
  const isCreateRoute = location.pathname.endsWith('/create');
  const fromOrderParam = searchParams.get('fromOrder');
  const orderIdParam = searchParams.get('orderId');

  // Determine which modal should be open
  const isFromOrder = fromOrderParam === 'true';
  const isSelectOrderModalOpen = isCreateRoute && isFromOrder && !orderIdParam;
  const isFormModalOpen = isCreateRoute && (orderIdParam || fromOrderParam === 'false');
  const formModalOrderId = orderIdParam;

  // Navigation helpers for URL-based state
  const navigateToCreate = useCallback(
    (fromOrder: boolean, orderId?: string) => {
      const params = new URLSearchParams();
      params.set('fromOrder', String(fromOrder));
      if (orderId) {
        params.set('orderId', orderId);
      }
      navigate(`/achats/receptions/create?${params.toString()}`);
    },
    [navigate]
  );

  const navigateToList = useCallback(() => {
    navigate('/achats/receptions');
  }, [navigate]);

  // Parse URL params for deep linking
  const urlParams = useMemo(() => parseUrlParams(searchParams), [searchParams]);

  // Update URL when filters change
  const updateUrl = useCallback(
    (updates: Record<string, string | null>) => {
      const newParams = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  // Filters state from URL
  const [supplierId, setSupplierId] = useState(urlParams.supplierId);
  const [status, setStatus] = useState(urlParams.status);
  const [dateRange, setDateRange] = useState<DateRange>(urlParams.dateRange);
  const [page, setPage] = useState(urlParams.page);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [sortBy, setSortBy] = useState<SortField>(urlParams.sortBy);
  const [sortOrder, setSortOrder] = useState<SortOrder>(urlParams.sortOrder);

  // Query params
  const queryParams = useMemo(() => ({
    page,
    limit,
    supplierId: supplierId || undefined,
    status: (status as GoodsReceiptStatus) || undefined,
    startDate: formatDateParam(dateRange.start) ?? undefined,
    endDate: formatDateParam(dateRange.end) ?? undefined,
    sortBy,
    sortOrder,
  }), [page, limit, supplierId, status, dateRange, sortBy, sortOrder]);

  // Fetch receipts and KPIs
  const { data: receiptsData, isLoading, refetch } = useGoodsReceipts(queryParams);
  const { data: kpis, refetch: refetchKpis } = useGoodsReceiptsKPIs(
    formatDateParam(dateRange.start) ?? undefined,
    formatDateParam(dateRange.end) ?? undefined
  );

  // Mutations
  const validateMutation = useValidateGoodsReceipt();
  const cancelMutation = useCancelGoodsReceipt();
  const deleteMutation = useDeleteGoodsReceipt();
  const generatePdfMutation = useGenerateGoodsReceiptPdf();

  const receipts = receiptsData?.data ?? [];
  const pagination = receiptsData?.pagination;

  // Modal handlers - use URL for deep linking
  const handleOpenFreeReceipt = useCallback(() => {
    navigateToCreate(false);
  }, [navigateToCreate]);

  const handleOpenFromOrder = useCallback(() => {
    navigateToCreate(true);
  }, [navigateToCreate]);

  const handleOrderSelected = useCallback(
    (orderId: string) => {
      navigateToCreate(true, orderId);
    },
    [navigateToCreate]
  );

  const handleCloseModal = useCallback(() => {
    navigateToList();
  }, [navigateToList]);

  const handleFormModalSuccess = useCallback(() => {
    // Refresh the list and KPIs after a successful creation
    refetch();
    refetchKpis();
  }, [refetch, refetchKpis]);

  // Handlers
  const handleRowClick = useCallback(
    (receipt: GoodsReceiptListItem) => {
      drawer.open(receipt.id);
    },
    [drawer]
  );

  const handleValidate = useCallback(
    async (receipt: GoodsReceiptListItem) => {
      if (window.confirm(`Voulez-vous valider le BR ${receipt.receiptNumber} ?`)) {
        await validateMutation.mutateAsync(receipt.id);
      }
    },
    [validateMutation]
  );

  const handleCancel = useCallback(
    async (receipt: GoodsReceiptListItem) => {
      if (window.confirm(`Voulez-vous annuler le BR ${receipt.receiptNumber} ?`)) {
        await cancelMutation.mutateAsync(receipt.id);
      }
    },
    [cancelMutation]
  );

  const handleDelete = useCallback(
    async (receipt: GoodsReceiptListItem) => {
      if (window.confirm(`Voulez-vous supprimer le BR ${receipt.receiptNumber} ?`)) {
        await deleteMutation.mutateAsync(receipt.id);
      }
    },
    [deleteMutation]
  );

  const handleGeneratePdf = useCallback(
    async (receipt: GoodsReceiptListItem) => {
      await generatePdfMutation.mutateAsync(receipt.id);
    },
    [generatePdfMutation]
  );

  const handleSort = useCallback((field: SortField, order: SortOrder) => {
    setSortBy(field);
    setSortOrder(order);
    setPage(1);
    updateUrl({ sortBy: field ?? null, sortOrder: order ?? null, page: null });
  }, [updateUrl]);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
    updateUrl({ page: String(newPage) });
  }, [updateUrl]);

  const handleLimitChange = useCallback((newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
    updateUrl({ page: null });
  }, [updateUrl]);

  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const handleExportPdf = useCallback(async () => {
    const dateFrom = formatDateParam(dateRange.start);
    const dateTo = formatDateParam(dateRange.end);
    if (!dateFrom || !dateTo) return;

    setIsExportingPdf(true);
    try {
      const blob = await goodsReceiptsService.exportPdfBatch(dateFrom, dateTo);
      const brandName = user?.currentBrand?.name ?? 'export';
      const fileName = buildExportFileName('bons_reception', brandName, dateFrom, dateTo) + '.pdf';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF telecharge');
    } catch {
      toast.error('Erreur lors de l\'export PDF');
    } finally {
      setIsExportingPdf(false);
    }
  }, [dateRange, user?.currentBrand?.name]);

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <Package className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-dark-500">Bons de Reception</h1>
              <p className="text-sm text-neutral-500 mt-0.5">
                Gerez vos receptions de marchandises
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleExportPdf}
              disabled={isExportingPdf}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-neutral-600 bg-white border border-neutral-300 rounded-lg hover:bg-neutral-50 transition-colors disabled:opacity-50"
              title="Exporter tous les bons de reception en PDF"
            >
              <Download className="w-4 h-4" />
              {isExportingPdf ? 'Export...' : 'Export PDF'}
            </button>
            {canCreate && (
              <NewReceiptButton
                onOpenFreeReceipt={handleOpenFreeReceipt}
                onOpenFromOrder={handleOpenFromOrder}
              />
            )}
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="bg-white border-b border-neutral-200 px-6 py-4 flex-shrink-0">
        <div className={`grid grid-cols-2 ${showFinancials ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-4`}>
          <KPICard
            title="Total receptions"
            value={kpis?.totalReceipts ?? 0}
            icon={<Package className="w-5 h-5" />}
          />
          {showFinancials && (
            <KPICard
              title="Montant HT total"
              value={kpis ? formatCentimes(kpis.totalAmountExclTax, currency) : '-'}
              icon={<Truck className="w-5 h-5" />}
            />
          )}
          <KPICard
            title="Brouillons en attente"
            value={kpis?.pendingDrafts ?? 0}
            icon={<FileText className="w-5 h-5" />}
            badge={kpis && kpis.pendingDrafts > 0 ? { label: 'A traiter', variant: 'warning' } : undefined}
          />
          <KPICard
            title="Avec ecarts"
            value={kpis?.receiptsWithVariance ?? 0}
            icon={<AlertTriangle className="w-5 h-5" />}
            badge={kpis && kpis.receiptsWithVariance > 0 ? { label: 'Attention', variant: 'danger' } : undefined}
          />
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-neutral-200 px-6 py-3 flex-shrink-0">
        <GoodsReceiptsFilters
          supplierId={supplierId}
          status={status}
          dateRange={dateRange}
          onSupplierChange={(v) => {
            setSupplierId(v);
            setPage(1);
            updateUrl({ supplierId: v || null, page: null });
          }}
          onStatusChange={(v) => {
            setStatus(v);
            setPage(1);
            updateUrl({ status: v || null, page: null });
          }}
          onDateRangeChange={(range) => {
            setDateRange(range);
            setPage(1);
            updateUrl({
              startDate: formatDateParam(range.start),
              endDate: formatDateParam(range.end),
              page: null,
            });
          }}
          showSupplierFilter={canReadSuppliers}
        />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6 bg-neutral-50">
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-16" />
            ))}
          </div>
        ) : receipts.length === 0 ? (
          <EmptyState
            icon={Package}
            title="Aucune reception trouvee"
            description="Les receptions apparaitront ici une fois creees depuis vos commandes"
          />
        ) : (
          <GoodsReceiptsTable
            receipts={receipts}
            pagination={pagination ?? { page: 1, limit, total: 0, totalPages: 0 }}
            isLoading={isLoading}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onRowClick={handleRowClick}
            onValidate={canValidate ? handleValidate : undefined}
            onCancel={canValidate ? handleCancel : undefined}
            onDelete={canDelete ? handleDelete : undefined}
            onGeneratePdf={isSuperAdmin ? handleGeneratePdf : undefined}
            onSort={handleSort}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
            onFetchAllRows={() =>
              fetchAllForExport(async ({ limit: exportLimit }) => {
                const response = await goodsReceiptsService.list({
                  ...queryParams,
                  page: 1,
                  limit: exportLimit,
                });
                return {
                  items: response.data,
                  pagination: { total: response.pagination.total },
                };
              })
            }
            showFinancials={showFinancials}
          />
        )}
      </div>

      {/* Receipt Drawer */}
      <GoodsReceiptDrawer
        receiptId={drawer.resourceId}
        open={drawer.isOpen}
        onOpenChange={(open) => !open && drawer.close()}
        showFinancials={showFinancials}
        canValidate={canValidate}
        canCancel={canValidate}
      />

      {/* Select Order Modal (step 1 when fromOrder=true) */}
      <SelectOrderModal
        isOpen={isSelectOrderModalOpen}
        onClose={handleCloseModal}
        onSelect={handleOrderSelected}
      />

      {/* Receipt Form Modal (step 2 or direct for free receipt) */}
      <GoodsReceiptFormModal
        isOpen={Boolean(isFormModalOpen)}
        onClose={handleCloseModal}
        orderId={formModalOrderId}
        onSuccess={handleFormModalSuccess}
      />
    </div>
  );
}
